/** @jsx jsx */
import React, { useMemo } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { Box, Anchor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ProjectIcon from '@atlaskit/icon/core/project';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import BookWithBookmarkIcon from '@atlaskit/icon/core/book-with-bookmark';
import { useIntl } from '@atlassian/jira-intl';

import messages from './OnboardingCheckListPostTasks.messages.tsx';

const postTasksButtonStyles = cssMap({
	root: {
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.150'),
	},
	anchor: {
		display: 'flex',
		textDecoration: 'none',
		paddingBlock: token('space.050'),
		marginTop: token('space.075'),
		alignItems: 'center',
	},
	linkText: {
		color: token('color.link'),
		paddingLeft: token('space.100'),
	},
});

type QuickstartLink = {
	label: string;
	href: string;
	icon: React.ReactElement;
};

export const OnboardingCheckListPostTasks = (): JSX.Element => {
	const { formatMessage } = useIntl();

	const quickstartLinks: Array<QuickstartLink> = useMemo(
		() => [
			{
				label: formatMessage(messages.productDiscoveryHandbook),
				href: 'https://www.atlassian.com/software/jira/product-discovery/resources/handbook/introduction#why-did-we-write-this-handbook',
				icon: (
					<ProjectIcon
						color={token('color.link.pressed')}
						label={formatMessage(messages.productDiscoveryHandbook)}
					/>
				),
			},
			{
				label: formatMessage(messages.communityFaq),
				href: 'https://community.atlassian.com/forums/Jira-Product-Discovery-articles/Frequently-asked-questions-about-the-product-how-do-I/ba-p/2002378',
				icon: (
					<PeopleGroupIcon
						color={token('color.link.pressed')}
						label={formatMessage(messages.communityFaq)}
					/>
				),
			},
			{
				label: formatMessage(messages.courseDeepDive),
				href: 'https://community.atlassian.com/learning/path/get-the-most-out-of-jira-product-discovery',
				icon: (
					<BookWithBookmarkIcon
						color={token('color.link.pressed')}
						label={formatMessage(messages.courseDeepDive)}
					/>
				),
			},
		],
		[formatMessage],
	);

	return (
		<Box xcss={postTasksButtonStyles.root}>
			{quickstartLinks.map((link) => (
				<Anchor
					xcss={postTasksButtonStyles.anchor}
					href={link.href}
					key={link.href}
					aria-label={link.label}
					target="_blank"
				>
					{link.icon}
					<Box xcss={postTasksButtonStyles.linkText}>{link.label}</Box>
				</Anchor>
			))}
		</Box>
	);
};
